import {Component, inject} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ApiService} from '@src/app/shared/services/api.service';
import {SharedModule} from '@src/app/shared/shared.module';
import {finalize} from 'rxjs';
import {DoorType} from '../../models/door-type.model';

export interface DoorTypeFormDialogData
{
    doorType: DoorType | null;
    sortOrder: number;
}

@Component({
    selector: 'app-door-type-form-dialog',
    imports: [SharedModule],
    templateUrl: './door-type-form-dialog.component.html',
    styleUrls: ['./door-type-form-dialog.component.scss'],
})
export class DoorTypeFormDialogComponent
{
    public readonly data = inject<DoorTypeFormDialogData>(MAT_DIALOG_DATA);
    public readonly form: FormGroup;
    public saving = false;
    public errorMessage = '';

    private readonly formBuilder = inject(FormBuilder);
    private readonly apiService = inject(ApiService);
    private readonly dialogRef = inject(MatDialogRef<DoorTypeFormDialogComponent>);

    public constructor()
    {
        const doorType = this.data.doorType;
        this.form = this.formBuilder.group({
            title: [{value: doorType?.title ?? '', disabled: doorType?.is_system ?? false}, [Validators.required, Validators.maxLength(125)]],
            maintenance_enabled: [doorType?.inspection_frequency_months !== null && doorType?.inspection_frequency_months !== undefined],
            inspection_frequency_months: [doorType?.inspection_frequency_months ?? 12, [Validators.required, Validators.min(1), this.integerValidator]],
            notify_days_before: [doorType?.notify_days_before ?? null, [Validators.min(0), this.integerValidator]],
        });

        this.setScheduleControlsEnabled(this.scheduleEnabled);
        this.form.controls.maintenance_enabled.valueChanges.subscribe(enabled => this.setScheduleControlsEnabled(enabled === true));
    }

    public get dialogTitle(): string
    {
        if (!this.data.doorType) {
            return 'Create door type';
        }

        return this.data.doorType.is_system ? 'Edit maintenance settings' : 'Edit door type';
    }

    public get scheduleEnabled(): boolean
    {
        return this.form.controls.maintenance_enabled.value === true;
    }

    public save(): void
    {
        if (this.form.invalid || this.saving) {
            this.form.markAllAsTouched();
            return;
        }

        const values = this.form.getRawValue();
        const payload: Record<string, string | number | null> = {
            inspection_frequency_months: values.maintenance_enabled ? Number(values.inspection_frequency_months) : null,
            notify_days_before: values.maintenance_enabled && values.notify_days_before !== null && values.notify_days_before !== ''
                ? Number(values.notify_days_before)
                : null,
        };

        if (!this.data.doorType?.is_system) {
            payload.title = values.title.trim();
        }

        if (!this.data.doorType) {
            payload.sort_order = this.data.sortOrder;
        }

        this.saving = true;
        this.errorMessage = '';
        const request = this.data.doorType
            ? this.apiService.update('door-types', this.data.doorType.id, payload)
            : this.apiService.create('door-types', payload);

        request.pipe(finalize(() => this.saving = false)).subscribe({
            next: () => this.dialogRef.close(true),
            error: response => this.errorMessage = response?.error?.message ?? 'The door type could not be saved.',
        });
    }

    private readonly integerValidator = (control: AbstractControl): ValidationErrors | null =>
    {
        if (control.value === null || control.value === '') {
            return null;
        }

        return Number.isInteger(Number(control.value)) ? null : {integer: true};
    };

    private setScheduleControlsEnabled(enabled: boolean): void
    {
        const controls = [this.form.controls.inspection_frequency_months, this.form.controls.notify_days_before];
        controls.forEach(control => enabled ? control.enable({emitEvent: false}) : control.disable({emitEvent: false}));
    }
}
